import { z } from "zod";

export const filterModeSchema = z.enum([
    "is",
    "isNot",
    "contains",
    "notContains",
]);

export const filterValueWithModeSchema = z.object({
    value: z.unknown(),
    mode: filterModeSchema,
    exception: z.unknown().optional(),
});

export type FilterMode = z.infer<typeof filterModeSchema>;
export interface FilterValueWithMode<T> {
    value: T;
    mode: FilterMode;
    /**
     * A second value matched with the opposite of `mode`; a row must satisfy
     * both. Absent when the filter has no exception, `null` for an exception
     * the user has added but not filled in yet.
     */
    exception?: T | null;
}

/** Which of a filter's two values a value editor edits. */
export type FilterTarget = "main" | "exception";

export type FilterModeFamily = "set" | "string";

/**
 * In the "set" family the filter value describes a set of accepted values
 * (selected options, a date range) matched by the column's own predicate; in
 * the "string" family it is a substring matched against the displayed value.
 */
export const FILTER_MODE_DEFINITIONS: Record<
    FilterMode,
    { family: FilterModeFamily; negated: boolean; opposite: FilterMode }
> = {
    is: { family: "set", negated: false, opposite: "isNot" },
    isNot: { family: "set", negated: true, opposite: "is" },
    contains: { family: "string", negated: false, opposite: "notContains" },
    notContains: { family: "string", negated: true, opposite: "contains" },
};

export function isStringMode(mode: FilterMode): boolean {
    return FILTER_MODE_DEFINITIONS[mode].family === "string";
}

export function isSameModeFamily(a: FilterMode, b: FilterMode): boolean {
    return (
        FILTER_MODE_DEFINITIONS[a].family === FILTER_MODE_DEFINITIONS[b].family
    );
}

/** The mode an exception uses: the negation of the main mode within its family. */
export function oppositeMode(mode: FilterMode): FilterMode {
    return FILTER_MODE_DEFINITIONS[mode].opposite;
}

/** Whether the filter carries an exception, filled in or not. */
export function hasException<T>(
    filterValue: FilterValueWithMode<T> | null | undefined
): filterValue is FilterValueWithMode<T> & { exception: T | null } {
    return filterValue?.exception !== undefined;
}
